'use client'
import {useEffect, useState} from "react";
import styles from '@/app/styles/components/pages/cart/cart-preview.module.scss'
import NumberInput from "@/app/components/inputs/NumberInput";
import CustomNumberFormat from "@/app/components/common/CustomNumberFormat";
import {addToCart} from "@/app/services/CartService";
import {updateCart, updateDeliveryMethod, updateDeliveryPrice} from "@/app/slices/CartSlice";
import {useDispatch, useSelector} from "react-redux";
import {FormattedMessage} from "react-intl";
import {
    calculatePrice,
    calculateShipping,
    calculateWeight,
    getProductById,
    isLocalDelivery
} from "@/app/utils/CartUtil";
import {getShippingZoneMethods} from "@/app/services/ShippingZoneService";
import {getCDNImage} from "@/app/utils/ImageUtil";
import { isEmpty, isEmptyObject } from "@/app/utils/Validations";

const CartPreview = ({data,products,cart, backText, handleBack, handleContinue, continueTitle, canContinue, agreed, setAgreed}) => {
    const dispatch = useDispatch();

    const deliveryCity = useSelector((state) => state.cart?.deliveryCity);
    const deliveryMethod = useSelector((state) => state.cart.deliveryMethod);
    const [shippingMethods, setShippingMethods] = useState([])
    const [selectedShippingMethod, setSelectedShippingMethod] = useState(deliveryMethod)
    const [shippingCost, setShippingCost] = useState(0)
    const [canContinueShipping, setCanContinueShipping] = useState(false)

    useEffect(() => {
        if (deliveryCity) {

            let zone = undefined

            if (isLocalDelivery(deliveryCity)) {
                zone = data?.shippingZones?.find(x => x.name === 'Novi Sad')
            } else {
                zone = data?.shippingZones?.find(x => x.name === 'Srbija')
            }

            getShippingZoneMethods(zone.id).then(response => {
                setShippingMethods(response)
            })
        }

    }, [deliveryCity])

    useEffect(() => {

        const shippingCost = calculateShipping(selectedShippingMethod, calculatePrice(products, cart),
            calculateWeight(products, cart))

        setShippingCost(shippingCost)
        dispatch(updateDeliveryMethod(selectedShippingMethod))
        dispatch(updateDeliveryPrice(shippingCost))

        if(!isEmptyObject(selectedShippingMethod)){
            setCanContinueShipping(true);
        }else{
            setCanContinueShipping(false);
        }

    }, [selectedShippingMethod])

    const handleShippingMethodClick = (method) => {
        setSelectedShippingMethod(method)
        setCanContinueShipping(true);

    }

    const isShippingMethodSelected = (method) => {
        return method?.id === selectedShippingMethod?.id
    }

    const handleQuantityUpdate = (id, quantity) => {
        addToCart({
            [id]: quantity
        }).then(response => {
            dispatch(updateCart({data:response,uuid:'preview'}))
        })
    }

    const renderItems = (products, cart) => {

        if (!cart) {
            return ''
        }

        let result = []

        for (let id of Object.keys(cart)) {

            const product = getProductById(products, id)
            const quantity = cart[id]

            if (!product) {
                continue
            }

            const image = product.images[0]?.src

            const imageStyle = {
                backgroundImage: `url(${getCDNImage(image)})`,
                backgroundRepeat: 'no-repeat',
                backgroundPositionX: 'center',
                backgroundPositionY: 'center',
                backgroundSize: "cover"
            }

            result.push(<div key={'cart-preview-product-' + product.id} className={styles.item}>
                <div className={styles.imageContainer}>
                    <div style={imageStyle} className={styles.image}/>
                    <div>
                        <h2>{product.name}</h2>
                        <NumberInput defaultValue={quantity}
                                     onClick={(quantity) => handleQuantityUpdate(product.id, quantity)}/>
                    </div>
                </div>
                <div className={styles.price}>
                    <CustomNumberFormat value={parseFloat(product?.price * quantity).toFixed(2)}/>

                    {
                        quantity > 1 &&
                        <div className={styles.priceOther}>
                            <CustomNumberFormat value={parseFloat(product?.price).toFixed(2)}/>
                            <FormattedMessage id={'components.pages.cart.cartPreview.each'}/>
                        </div>
                    }
                </div>
            </div>)
        }

        return result
    }

    const renderShippingMethods = (shippingMethods) => {

        let result = []

        for (let method of shippingMethods) {
            result.push(
                <div key={'method-' + method.id}
                     className={isShippingMethodSelected(method) ? styles.shippingMethodSelected : styles.shippingMethod}
                     onClick={() => handleShippingMethodClick(method)}
                     >
                    <span className={styles.radio}>
                        <span className={styles.radioInside}></span>
                    </span>
                    <label>{method.title}</label>
                </div>
            )
        }

        return result
    }

    return <div className={styles.cartPreviewContainer}>
        <h3><FormattedMessage id={'components.pages.cart.cartPreview.orderDetails'}/></h3>
        {renderItems(products, cart)}
        <div className={styles.priceContainer}>
            {
                deliveryCity &&
                <>
                    <div className={styles.deliveryContainerItem}>
                        <div className={styles.title}>
                            <FormattedMessage id={'components.pages.cart.cartPreview.deliveryOptions'}/>
                        </div>
                        <div className={styles.methodsContainer}>
                            {renderShippingMethods(shippingMethods)}
                        </div>
                    </div>
                    <div className={styles.divider}/>
                </>
            }
            <div className={styles.priceContainerItem}>
                <div className={styles.priceTitle}>
                    <FormattedMessage id={'components.pages.cart.cartPreview.price'}/>
                </div>
                <div className={styles.price}>
                    {<CustomNumberFormat value={parseFloat(calculatePrice(products, cart)).toFixed(2)}/>}
                </div>
            </div>
            <div className={styles.divider}/>
            <div className={styles.priceContainerItem}>
                <div className={styles.priceTitle}>
                    <FormattedMessage id={'components.pages.cart.cartPreview.delivery'}/>
                </div>
                <div className={styles.price}>
                    {<CustomNumberFormat value={parseFloat(shippingCost).toFixed(2)}/>}
                </div>
            </div>
            <div className={styles.divider}/>
            <div className={styles.priceContainerItem}>
                <div className={styles.titleTotal}>
                    <FormattedMessage id={'components.pages.cart.cartPreview.total'}/>
                </div>
                <div className={styles.priceTotal}>
                    {<CustomNumberFormat
                        value={(parseFloat(calculatePrice(products, cart)) + (shippingCost ? shippingCost : 0)).toFixed(2)}/>}
                </div>
            </div>

            <div className={styles.agreeContainer}>
                <input checked={agreed} onChange={() => setAgreed(!agreed)} type="checkbox"></input>
                <label>Slažem se sa <a href="/uslovi-koriscenja">uslovima korišćenja</a></label>
            </div>

            <div className={styles.controls}>
                <button onClick={handleBack} className={styles.btnLight}>
                    {backText}
                </button>
                <button onClick={handleContinue} className={(canContinue && agreed && canContinueShipping) ? styles.btn : styles.btnDisabled}>
                    {continueTitle}
                </button>
            </div>
        </div>
    </div>
}

export default CartPreview
